<template>
  <div
    class="canvas-background-image"
    :class="{ 'canvas-background-image--fullscreen': isFullscreen }"
    :style="{
      backgroundImage: (isLoading || !backgroundImageId) ? undefined : `url(${validThumbnailUrl})`,
    }"
  >
    <LoadingIndicator
      v-if="isLoading"
      class="canvas-background-image__loading-indicator"
      absolute
      transparent
    />
  </div>
</template>

<script>
import { get } from 'lodash-es';
import { mapState } from 'pinia';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import assetMixin from '@/mixins/asset';
import thumbnailLoader from '@/mixins/thumbnailLoader';
import {
  useAssetStore,
  useEditorStore,
} from '@/stores';

export default {
  name: 'CanvasBackgroundImage',
  components: {
    LoadingIndicator,
  },
  mixins: [
    assetMixin, // handles pulling thumbnails urls from an asset
    thumbnailLoader, // handles finding valid thumbnails
  ],
  props: {
    pageId: {
      type: String,
      default: null,
    },
    isFullscreen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      ignoreResize: true, // used by the asset mixin
      useLargeImageUrls: true, // used by the thumbnail loader
    };
  },
  computed: {
    ...mapState(useAssetStore, [
      'assets',
    ]),
    ...mapState(useEditorStore, [
      'currentPageId',
      'draft',
    ]),
    asset() {
      return this.assets[this.backgroundImageId];
    },
    backgroundImageId() {
      if (!this.draft.pages) return null;

      // In the normal canvas, this should be the current page's background image.
      // In print preview, a custom page id is passed instead.
      const pageId = this.pageId || this.currentPageId;
      const page = this.draft.pages.find((p) => (p.id === pageId));

      return get(page, 'options.theme.background_image');
    },
    isLoading() {
      if (!this.backgroundImageId) return false;

      return !this.asset
        || this.thumbnailIsLoading
        || !this.validThumbnailUrl;
    },
  },
};
</script>

<style lang="stylus">
.canvas-background-image {
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  bottom: 0;
  left: 0;
  overflow: hidden;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1;

  &:not(.canvas-background-image--fullscreen) {
    border-radius: $nebula-border-radius-small;
  }

  &__loading-indicator {
    pointer-events: none;
  }
}
</style>
